<template>
  <div class="menu-container">
    <RippleButton :disabled="disabled" :handle_click="handleMenuClick">
      <Loader :size="18" v-if="loading" />

      <span>{{ parseTranslation(EXPORT_BASE) }}</span>

      <ArrowDown force_fill="#fff" />

      <div v-if="open" class="options">
        <button
          v-for="download in downloadExtensions"
          :key="download.value"
          @click="handleDownload(download.value)"
        >
          {{ download.label }}
        </button>
      </div>
    </RippleButton>

    <div v-if="open" class="options-background" @click="toggleMenu"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Loader from '@/components/loading/Loader-3'

import ArrowDown from '@/components/imgs/ArrowDown.vue'

import RippleButton from '../../GradientRippleButton.vue'

import reportingToolAPI from '@/services/reportingTool'

import { removeUndefinedProperties } from '@/utils/object'

import * as translationExportButton from './ExportButton.translations'

const { default: translations, ...translationKeys } = translationExportButton

const downloadExtensions = [
  { label: 'CSV', value: 'csv' },
  { label: 'XLSX', value: 'xlsx' },
  { label: 'SPSS', value: 'sav' },
]

export default {
  data() {
    return {
      open: false,
      downloadExtensions,
      loading: false,
      translations,
      ...translationKeys,
    }
  },

  props: {
    survey_status: {
      required: true,
      type: Object,
    },

    disabled: {
      required: false,
      type: Boolean,
    },

    active_filters: {
      required: true,
      type: Object,
    },
  },

  components: {
    ArrowDown,
    Loader,
    RippleButton,
  },

  computed: {
    filters() {
      const { dateRange, statuses } = this.active_filters

      const noneSelected = [dateRange, statuses].every(list => !list.length)

      if (noneSelected) return {}

      // 'sv-SE' locale gives us ISO format by default
      const [startDate, endDate] = dateRange.map(date =>
        new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short' }).format(date),
      )

      return removeUndefinedProperties({
        startDate,
        endDate,
        status: statuses.length ? statuses : undefined,
      })
    },
  },

  methods: {
    ...mapActions(['sendMessage']),

    toggleMenu() {
      this.open = !this.open
    },

    toggleLoading() {
      this.loading = !this.loading
    },

    handleMenuClick() {
      if (this.loading) return

      this.toggleMenu()
    },

    async handleDownload(extension) {
      this.toggleLoading()
      this.open = false

      try {
        const { data } = await reportingToolAPI.post(
          `/survey/export`,
          {
            type: extension,
            surveyId: this.survey_status.survey_id,
            ...this.filters,
          },
          {
            responseType: 'arraybuffer',
          },
        )

        const blob = new Blob([data], {
          type: 'application/octet-stream',
        })

        const filename = `${this.survey_status.name}.${extension}`

        const a = document.createElement('a')
        document.body.appendChild(a)

        const url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = filename

        a.click()

        window.URL.revokeObjectURL(url)

        a.remove()
      } catch (err) {
        this.sendMessage(`Error while downloading ${extension} file`)
      } finally {
        this.toggleLoading()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.options {
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  left: 0;
  background: #fff;
  padding: 4px 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1;

  transform: scale(0);
  transform-origin: top left;
  will-change: transform;
  animation: growWrapper 0.4s ease forwards;

  > button {
    @include transparentButton;
    justify-content: flex-start;
    font-size: rem(15);
    width: 100%;
    padding: 4px 16px;
    transition: background-color 0.25s ease;

    &:hover {
      background-color: #f8f9fa;
    }
  }
}

.options-background {
  position: fixed;
  background: transparent;
  inset: 0;
}

@keyframes growWrapper {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}
</style>
